import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button, Input, Alert } from '@/components/common';
import { Language } from '@/types';
import { getTranslation } from '@/constants';

interface LoginPageProps {
  language: Language;
  onLanguageToggle: () => void;
}

export const LoginPage: React.FC<LoginPageProps> = ({ language, onLanguageToggle }) => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const { signIn, signUp, error, loading, clearError } = useAuth();
  const navigate = useNavigate();
  const t = getTranslation(language);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    try {
      if (isSignUp) {
        await signUp(email, password, displayName, postalCode);
      } else {
        await signIn(email, password);
      }
      navigate('/');
    } catch (err) {
      // Error is already set by the hook
      console.error('Auth error:', err);
    }
  };

  const brandName = 'MutuAid';

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-blue-700 via-blue-600 to-indigo-600 flex items-center justify-center px-4 py-8">
      {/* Language Toggle - Compact for mobile */}
      <button
        onClick={onLanguageToggle}
        className="absolute top-4 right-4 inline-flex items-center rounded-lg bg-white/20 px-3 py-2 text-sm font-semibold text-white ring-1 ring-white/30 hover:bg-white/30 transition-all focus:outline-none focus:ring-2 focus:ring-white/50 min-h-[36px]"
        aria-label={language === 'en' ? 'Switch to French' : 'Switch to English'}
      >
        {language === 'en' ? 'FR' : 'EN'}
      </button>

      {/* Main Content - Mobile-optimized width */}
      <div className="w-full max-w-sm text-white">
        {/* Brand Header - Compact for mobile */}
        <div className="mb-6 text-center">
          <div className="mx-auto mb-4 h-16 w-16 rounded-2xl bg-white/20 ring-1 ring-white/30 flex items-center justify-center shadow-md">
            <span className="text-4xl" role="img" aria-label="Helping hands">🤝</span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight">{brandName}</h1>
          <p className="mt-2 text-sm text-white/80">{t.tagline}</p>
        </div>

        {/* Auth Form - Mobile-friendly padding */}
        <div className="bg-white rounded-xl shadow-xl p-5 sm:p-6">
          <h2 className="text-xl font-bold mb-4 text-center text-gray-900">
            {isSignUp ? t.createAccount : t.signIn}
          </h2>

          {error && (
            <div className="mb-4">
              <Alert variant="error" onClose={clearError}>
                <div>
                  <p className="font-semibold text-sm">Authentication Error</p>
                  <p className="text-sm">{error}</p>
                </div>
              </Alert>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {isSignUp && (
              <>
                <Input
                  type="text"
                  label={t.displayName}
                  placeholder={t.displayName}
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  autoComplete="name"
                />
                <Input
                  type="text"
                  label={t.postalCode}
                  placeholder="H2X"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value.toUpperCase().slice(0, 3))}
                  helperText={t.postalCodeHelper}
                  maxLength={3}
                />
              </>
            )}

            <Input
              type="email"
              label={t.email}
              placeholder={t.email}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />

            <Input
              type="password"
              label={t.password}
              placeholder={t.password}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete={isSignUp ? 'new-password' : 'current-password'}
              helperText={isSignUp ? 'Minimum 6 characters' : undefined}
            />

            <div className="pt-1">
              <Button type="submit" fullWidth loading={loading} size="md">
                {isSignUp ? t.signUp : t.signIn}
              </Button>
            </div>
          </form>

          {/* Toggle Sign In/Sign Up - Compact touch target */}
          <div className="mt-5 pt-4 border-t border-gray-100">
            <p className="text-center text-sm text-gray-600">
              {isSignUp ? t.alreadyHaveAccount : t.dontHaveAccount}
            </p>
            <button
              onClick={() => {
                setIsSignUp(!isSignUp);
                clearError();
              }}
              className="mt-2 w-full py-2.5 text-sm font-semibold text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              {isSignUp ? t.signIn : t.signUp}
            </button>
          </div>
        </div>

        {/* Footer - Compact text for mobile */}
        <div className="mt-4 text-center text-xs text-white/70 leading-relaxed">
          {t.consentPrefix}{' '}
          <a
            href="/terms"
            className="underline decoration-white/40 hover:decoration-white text-white"
          >
            {t.termsLabel}
          </a>{' '}
          {t.andWord}{' '}
          <a
            href="/privacy"
            className="underline decoration-white/40 hover:decoration-white text-white"
          >
            {t.privacyLabel}
          </a>
          .
        </div>

        <div className="mt-3 text-center text-xs text-white/60">{t.builtFor}</div>

        <div className="mt-2 text-center text-xs text-white/50">
          © {new Date().getFullYear()} {brandName}. {t.rights}
        </div>
      </div>
    </div>
  );
};
